import React from 'react';
import TrafficLandingTemplate, { LandingPageConfig } from './TrafficLandingTemplate';
import { Activity, BarChart3, Globe } from 'lucide-react';

const config: LandingPageConfig = {
    slug: 'ja/targeted-traffic-kounyu',
    titleTag: 'Buy Targeted Traffic | Country & Niche Targeting | Traffic Creator',
    metaDescription: 'Buy targeted traffic from specific countries, devices, and niches. Real residential visitors precisely matched to your audience. Verified in GA4.',
    keywords: 'Buy Targeted Traffic, Buy Targeted Website Traffic, Buy Targeted Web Traffic, Targeted Traffic That Converts, Buy Keyword Targeted Traffic',
    primaryKeyword: 'Buy Targeted Traffic',
    secondaryKeyword: 'Targeted Traffic That Converts',
    reviewCount: '518',
    ratingValue: '4.9',

    h1: 'Buy Targeted Traffic That Converts into Real Results',
    subheadline: 'Generic traffic is wasted budget. Our platform lets you buy targeted traffic from exact countries, devices, and referrer paths — matching your ideal audience profile perfectly.',

    problemH2: 'Why Untargeted Traffic Wastes Your Budget',
    problemText: 'Sending random visitors from irrelevant countries and devices to your website creates terrible bounce rates and zero conversions. You need traffic that matches your actual audience — the right geography, the right device, and the right browsing behavior.',
    solutionPoints: [
        'Precision geo-targeting from 190+ countries down to city level',
        'Device targeting — desktop, mobile, or tablet traffic on demand',
        'Custom referrer paths to simulate traffic from Google, social media, or direct',
        'Keyword-targeted sessions that arrive via search-like pathways'
    ],

    featuresH2: 'How Our Targeted Traffic Platform Works',
    features: [
        { icon: <Globe />, title: 'Country & City Targeting', description: 'Choose traffic from any country worldwide. Need visitors specifically from New York, London, or Tokyo? Our residential network delivers with pinpoint accuracy.' },
        { icon: <BarChart3 />, title: 'Behavioral Targeting', description: 'Configure exactly how visitors interact — dwell time, pages per session, scroll depth, and bounce rate — to match your niche\'s natural engagement patterns.' },
        { icon: <Activity />, title: 'Referrer Source Control', description: 'Make traffic appear as if it came from Google search, social media, or direct visits. Full control over the referrer path for realistic Analytics data.' }
    ],

    testimonialsH2: 'What Our Targeted Traffic Clients Say',
    testimonials: [
        { name: 'David L.', role: 'E-commerce Director', result: 'USA Traffic +400%', text: 'We needed US-only traffic for our Shopify store. The geo-targeting accuracy was incredible — 98% of visits came from our target states.', seed: 'davidl' },
        { name: 'Nina S.', role: 'Media Buyer', result: 'Perfect Device Mix', text: 'Being able to buy targeted traffic split 70/30 mobile/desktop matched our actual audience perfectly. The Analytics looked completely natural.', seed: 'ninas' },
        { name: 'Carlos M.', role: 'Local Business Owner', result: 'City-Level Precision', text: 'I bought keyword targeted traffic for my local service area. All visits came from my city and neighboring areas. Incredible precision.', seed: 'carlosm' }
    ],

    ctaH2: 'Ready to Buy Targeted Traffic That Matches Your Audience?',

    faqs: [
        { question: 'What does buy targeted traffic mean exactly?', answer: 'Buying targeted traffic means purchasing website visitors that match specific criteria — geographic location, device type, referrer source, and behavioral patterns. Unlike generic traffic, targeted traffic aligns with your actual audience profile.' },
        { question: 'Can I buy targeted traffic from a specific country?', answer: 'Yes! We offer geo-targeting from 190+ countries. You can target by country, region, or even city level. Our residential IP network ensures every visit genuinely originates from your chosen location.' },
        { question: 'Does buying targeted traffic that converts actually work?', answer: 'The traffic itself generates realistic engagement signals (dwell time, scrolling, page views) that improve your site\'s SEO metrics. For e-commerce conversions, it helps by improving your search rankings which leads to more genuine converting visitors.' },
        { question: 'How is keyword targeted traffic different?', answer: 'Keyword targeted traffic arrives at your site through search-like pathways, mimicking visitors who found you through specific search queries. This creates organic search signals that can improve your rankings for those exact keywords.' },
        { question: 'Will targeted traffic appear in Google Analytics?', answer: 'Yes, 100%. All targeted traffic registers in GA4 with accurate geographic data, device information, session duration, and referrer paths — exactly matching the targeting parameters you set.' }
    ],

    relatedLinks: [
        { label: 'Geo-Targeted Traffic', to: '/ja/geo-targeted-traffic' },
        { label: 'Buy Website Traffic', to: '/ja/buy-website-traffic' },
        { label: 'Local SEO Traffic Bot', to: '/ja/local-seo-traffic-bot' }
    ],

    productName: 'Buy Targeted Traffic - Precision Audience Matching',
    productDescription: 'Buy targeted traffic from specific countries, devices, and niches. Real residential visitors precisely matched to your audience.',
    translations: {
        'navFeatures': '機能',
        'navPricing': '料金',
        'navFaq': 'よくある質問',
        'navLogin': 'ダッシュボード',
        'heroBadge': 'プレミアムトラフィック',
        'heroCtaPrimary': '無料で試す',
        'heroCtaSecondary': '料金を見る',
        'heroTrust': '10,000人以上のユーザーに信頼',
        'heroMicrocopy': 'クレジットカード不要。100%安全＆Analytics認証済み。',
        'heroLiveLabel': 'ライブ',
        'heroTrafficIncrease': 'トラフィック増加',
        'heroActiveLabel': 'アクティブ',
        'heroVisitorsServed': '提供済み訪問者',
        'socialProofLabel': '信頼企業：',
        'problemWithout': 'なし',
        'problemWith': 'あり',
        'problemBullets': [
                '低いオーガニックトラフィックが成長を制限',
                '悪い検索順位がコンバージョンに影響',
                '競合がキーワードを独占',
                '高い直帰率がSEOを損なう'
        ],
        'statsCampaigns': '日次アクティブキャンペーン',
        'statsCountries': 'カ国',
        'statsUptime': '稼働率',
        'statsSupport': 'サポート',
        'featuresSubtitle': 'ウェブサイトのパフォーマンスを向上させるために必要なすべて。',
        'howItWorksH2': '3つの簡単なステップ',
        'howItWorksSteps': [
                {
                        'title': 'キャンペーン設定',
                        'desc': '国、デバイス、行動設定を数分で選択。'
                },
                {
                        'title': 'ネットワーク開始',
                        'desc': 'プラットフォームが実際の訪問者をURLに誘導。'
                },
                {
                        'title': '結果分析',
                        'desc': 'Google Analytics 4で指標の改善を確認。'
                }
        ],
        'pricingH2': '透明な',
        'pricingSubtitle': '一回限りの購入。サブスクなし。クレジットは無期限。',
        'pricingMostPopular': '最も人気',
        'pricingVisitors': '訪問者',
        'pricingGetStarted': '今すぐ開始',
        'pricingGuarantee': '30日間返金保証',
        'pricingDelivery': '即時配信（約5分）',
        'pricingCountries': '190+カ国',
        'faqH2': 'よくある質問',
        'ctaSubtitle': '10,000人以上のお客様に参加して、今日からトラフィックを増やしましょう。',
        'ctaButtonPrimary': '無料で開始',
        'ctaButtonSecondary': '料金を見る',
        'ctaMicrocopy': 'クレジットカード不要。100%安全＆Analytics認証済み。',
        'footerDescription': '世界中のSEOプロフェッショナルとデジタルエージェンシー向けソリューション。',
        'footerRelatedServices': '関連サービス',
        'footerCompany': '会社',
        'footerAbout': '会社概要',
        'footerBlog': 'ブログ',
        'footerHelpCenter': 'ヘルプセンター',
        'footerPrivacy': 'プライバシーポリシー',
        'footerImpressum': '法的情報',
        'footerStatus': '全システム稼働中',
        'footerBuyTraffic': 'ウェブサイトトラフィック購入'
},
};

const JaBuyTargetedTraffic: React.FC = () => {
    return <TrafficLandingTemplate config={config} />;
};

export default JaBuyTargetedTraffic;
